import { OrderTotalsSummary } from '@app/components/various/OrderTotalsSummary';
import { OrderTotalsSummaryItem } from '@app/components/various/OrderTotalsSummary/OrderTotalsSummaryItem';
import { OrderTotalsSummaryVoucherItem } from '@app/components/various/OrderTotalsSummary/OrderTotalsSummaryVoucherItem';
import { getIsSubmittingVoucher, getIsVoucherValid, getOrderDetails, getPaymentTerm } from '@app/ducks/order';
import { useAvailableOrderDetails } from '@app/utils/hooks/useAvailableOrderDetails';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { VoucherForm } from '../VoucherForm';

interface Props {
  isReadOnly?: boolean;
}

export const CheckoutSubtotal = ({ isReadOnly = false }: Props) => {
  const { order } = useSelector(getOrderDetails);
  const { orderTotals } = useAvailableOrderDetails();
  const { discounts } = React.useMemo(() => order ?? {}, [order]);
  const vouchers = React.useMemo(() => discounts?.vouchers ?? {}, [discounts]);

  const { t } = useTranslation(['common', 'checkout', 'totals', 'shipping']);

  const isSubmittingVoucher = useSelector(getIsSubmittingVoucher);
  const isSubmittedVoucherValid = useSelector(getIsVoucherValid);
  const paymentTerm = useSelector(getPaymentTerm);
  const [isVoucherFormVisible, setIsVoucherFormVisible] = React.useState(false);

  const handleToggleVoucherForm = React.useCallback(() => {
    setIsVoucherFormVisible(isFormVisible => !isFormVisible);
  }, []);

  React.useEffect(() => {
    if (isSubmittedVoucherValid && !isSubmittingVoucher) {
      setIsVoucherFormVisible(false);
    }
  }, [isSubmittedVoucherValid, isSubmittingVoucher]);

  const vouchersRows = React.useMemo(() => {
    if (isEmpty(vouchers)) {
      return [];
    }

    return Object.values(vouchers).map(voucher => ({
      id: voucher.voucher,
      name: t('checkout:voucher_discount'),
      type: 'voucher',
      value: voucher.priceOff,
    }));
  }, [t, vouchers]);

  const taxesRows = React.useMemo(() => {
    if (!isDefined(orderTotals?.taxes) || isEmpty(orderTotals.taxes)) {
      return [];
    }

    return orderTotals.taxes.map(tax => ({
      id: `tax${tax.taxPercent}`,
      name: t('common:tax_percent', { taxPercent: tax.taxPercent }),
      type: 'standard',
      value: tax.totalPriceTax,
    }));
  }, [t, orderTotals]);

  const totalsRows = React.useMemo(
    () => [
      {
        id: 'subtotal',
        name: t('totals:subtotal'),
        type: 'standard',
        value: orderTotals?.itemsTotalPrice,
      },
      ...vouchersRows,
      ...(orderTotals?.totalDiscountPrice ?
        [
          {
            id: 'totalDiscountPrice',
            name: t('totals:total_savings'),
            type: 'standard',
            value: orderTotals?.totalDiscountPrice,
          },
        ]
      : []),
      {
        id: 'shippingPrice',
        name: t('shipping:shipping_cost'),
        type: 'standard',
        value: orderTotals?.shippingPrice,
      },
      {
        id: 'grandTotalWithoutTax',
        name: t('totals:total_no_tax'),
        type: 'standard',
        value: orderTotals?.grandTotalWithoutTax,
      },
      ...taxesRows,
      ...(isDefined(orderTotals?.prepaymentPriceAsText) && isDefined(paymentTerm) ?
        [
          {
            id: 'prepaymentPrice',
            name: t('totals:total_prepayment', { percent: paymentTerm.prepaymentPercent }),
            type: 'standard',
            value: orderTotals.prepaymentPriceAsText,
          },
        ]
      : []),
    ],
    [paymentTerm, t, taxesRows, orderTotals, vouchersRows],
  );

  return (
    <div className="w-full">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="heading-3">{t('checkout:selection_summary')}</h3>
        {!isReadOnly && (
          <button
            type="button"
            className="inline-block text-neutral-20 underline underline-offset-4 transition-colors body-2 hover:text-neutral-40 focus-visible:rounded-small focus-visible:focus-outline"
            onClick={handleToggleVoucherForm}
          >
            {isVoucherFormVisible ? t('common:dismiss') : t('checkout:voucher_add')}
          </button>
        )}
      </div>
      {isVoucherFormVisible && <VoucherForm />}
      <OrderTotalsSummary
        finalPrice={{
          name: t('totals:grandtotal'),
          value: orderTotals?.grandTotalPrice ?? '-',
        }}
      >
        {totalsRows.map(summaryRow =>
          summaryRow.type === 'voucher' ?
            <OrderTotalsSummaryVoucherItem
              key={summaryRow.id}
              name={summaryRow.name}
              value={summaryRow.value}
              isReadOnly={isReadOnly}
              id={summaryRow.id}
            />
          : <OrderTotalsSummaryItem key={summaryRow.id} name={summaryRow.name} value={summaryRow.value} />,
        )}
      </OrderTotalsSummary>
    </div>
  );
};
